import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BrandingPage } from './BrandingPage';
import { PersonalInfoPage } from './PersonalInfoPage';
import { AccountManagementContent } from './AccountManagementContent';
import { InterviewProcessPage } from './InterviewProcessPage';
import { publicApiClient } from '../api/apiClient';
import { useAuthStore } from '../store/authStore';
import { CompaniesManagementContent } from './CompaniesManagementContent';
import { apiService } from '../services/apiService';
import { useBrandingColors } from '../hooks/useBrandingColors';
import { ComplaintsPage } from './complaints/ComplaintsPage';
import { IntegrationsContent } from './IntegrationsContent';
import { CreditsLimitsContent } from './CreditsLimitsContent';
import { isAtsAvailable, isRelevanterAvailable } from '../ats/atsConfig';
import { AtsImportLogsPanel } from '../ats/components/AtsImportLogsPanel';
import { useBrandingStore } from '../store/brandingStore';

type ManagementSection = 'branding' | 'limits' | 'personal' | 'accounts' | 'interview-process' | 'companies' | 'complaints' | 'integrations' | 'logs';

/**
 * Team Management Page Component
 * Главная страница управления с двойным боди: левая панель с табами, правая панель с контентом
 */
const ALL_MANAGEMENT_TABS: ManagementSection[] = ['branding', 'limits', 'personal', 'accounts', 'interview-process', 'companies', 'complaints', 'integrations', 'logs'];
const RELEVANTER_ONLY_TABS: ManagementSection[] = [
  'integrations',
  'branding',
  'interview-process',
  'limits',
  'complaints',
];

function getInitialSection(
  searchParams: URLSearchParams,
  availableTabs: ManagementSection[],
): ManagementSection {
  const tab = searchParams.get('tab') as ManagementSection | null;
  if (tab && availableTabs.includes(tab)) {
    return tab;
  }
  if (availableTabs.includes('personal')) return 'personal';
  if (availableTabs.includes('interview-process')) return 'interview-process';
  return availableTabs[0] || 'personal';
}

export function TeamManagementPage() {
  const [searchParams] = useSearchParams();
  const { companyData, loadCompanyData } = useBrandingStore();
  const { role, user } = useAuthStore();
  const colors = useBrandingColors();

  const normalizedRole = String((user as any)?.role || role || '').toUpperCase();
  const isSuperAdmin = normalizedRole === 'SUPER_ADMIN';
  const isOwner = normalizedRole === 'OWNER';
  const isAdmin = normalizedRole === 'ADMIN';
  // Рекрутер не должен видеть управление аккаунтами вообще
  const canManageAccounts = isSuperAdmin || isOwner || isAdmin;
  const atsAvailable = Boolean(companyData && isAtsAvailable(companyData));
  const atsOnly = Boolean(
    companyData
    && atsAvailable
    && !isRelevanterAvailable(companyData),
  );
  const availableTabs = useMemo(() => (
    ALL_MANAGEMENT_TABS.filter((tab) => {
      if (tab === 'logs' && !atsAvailable) return false;
      if (atsOnly && RELEVANTER_ONLY_TABS.includes(tab)) return false;
      if (tab === 'accounts' && !canManageAccounts) return false;
      if ((tab === 'companies' || tab === 'complaints') && !isSuperAdmin) return false;
      return true;
    })
  ), [atsAvailable, atsOnly, canManageAccounts, isSuperAdmin]);

  const [activeSection, setActiveSection] = useState<ManagementSection>(() => (
    getInitialSection(searchParams, availableTabs)
  ));
  // Открыта карточка одной компании (внутри вкладки «Компании») — прячем левое меню и
  // отдаём всю ширину под карточку, чтобы соответствовать полноширинному макету.
  const [companyDetailOpen, setCompanyDetailOpen] = useState(false);
  // REL-223: при клике на название компании в списке открываем раздел «Статистика и лимиты»
  // по выбранной компании. null — обычный режим (своя компания, из токена).
  const [limitsCompanyId, setLimitsCompanyId] = useState<number | null>(null);

  const [currentTeamId, setCurrentTeamId] = useState<number | null>(null);

  // Режим приглашения: true только когда пользователь явно нажал "Пригласить"
  const [invitationMode, setInvitationMode] = useState(false);

  useEffect(() => {
    if (!companyData) {
      loadCompanyData().catch(() => {});
    }
  }, [companyData, loadCompanyData]);

  // Функция для обновления списка пользователей команды
  const refreshTeamMembers = () => {
    setActiveSection('accounts');
    setTimeout(() => { setActiveSection('accounts'); }, 100);
  };

  // Функция для переключения в режим приглашения
  const switchToInvitationMode = (teamId?: number) => {
    console.log('=== switchToInvitationMode called ===', { teamId, currentTeamId: currentTeamId });
    setActiveSection('accounts');
    setInvitationMode(true);
    if (teamId) {
      console.log('Setting currentTeamId to:', teamId);
      setCurrentTeamId(teamId);
    } else {
      console.log('No teamId passed, keeping currentTeamId:', currentTeamId);
    }
  };

  // Обрабатываем параметр tab из URL и прячем недоступные разделы (ATS-only).
  useEffect(() => {
    const tabParam = searchParams.get('tab');

    if (tabParam && availableTabs.includes(tabParam as ManagementSection)) {
      setActiveSection(tabParam as ManagementSection);
      if (tabParam !== 'accounts') {
        setInvitationMode(false);
      }
      return;
    }

    setActiveSection((current) => (
      availableTabs.includes(current)
        ? current
        : getInitialSection(searchParams, availableTabs)
    ));
  }, [searchParams, availableTabs]);

  // Инициализируем currentTeamId при монтировании страницы
  useEffect(() => {
    let isMounted = true;
    const initTeamId = async () => {
      try {
        const user = await apiService.getAccount();
        const userAny: any = user as any;
        
        // Сначала проверяем teamId пользователя
        if (isMounted && userAny && typeof userAny.teamId === 'number') {
          console.log('=== TeamManagementPage: User has teamId:', userAny.teamId);
          setCurrentTeamId(userAny.teamId);
          return;
        }
        
        // Фоллбек: пробуем найти команду пользователя (владелец или первая)
        console.log('=== TeamManagementPage: Searching for user teams...');
                    const teamsResp = await publicApiClient.teams.listTeams();
        const teamsData = (teamsResp.data as any) || {};
        const content = Array.isArray(teamsData.content) ? teamsData.content : [];
        
        if (content.length === 0) {
          console.log('=== TeamManagementPage: No teams found');
          return;
        }
        
        // Ищем команду где пользователь владелец
        const owned = content.find((t: any) => t.owner === user.id);
        const fallbackTeam = owned || content[0];
        
        if (isMounted && fallbackTeam?.id) {
          console.log('=== TeamManagementPage: Setting currentTeamId to:', fallbackTeam.id, 'owned:', !!owned);
          setCurrentTeamId(fallbackTeam.id);
        }
      } catch (e) {
        console.error('Failed to init currentTeamId', e);
      }
    };
    initTeamId();
    return () => { isMounted = false; };
  }, []);



  // Простые SVG иконки для каждого таба
  const BrandingIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 2L12.09 8.26L20 9L12.09 9.74L10 16L7.91 9.74L0 9L7.91 8.26L10 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const PersonalIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M12 14C8.13401 14 5 17.134 5 21" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );

  const AccountIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M16 7C16 9.20914 14.2091 11 12 11C9.79086 11 8 9.20914 8 7C8 4.79086 9.79086 3 12 3C14.2091 3 16 4.79086 16 7Z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M12 14C8.13401 14 5 17.134 5 21" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M8 7C8 9.20914 6.20914 11 4 11C1.79086 11 0 9.20914 0 7C0 4.79086 1.79086 3 4 3C6.20914 3 8 4.79086 8 7Z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M4 14C0.13401 14 -3 17.134 -3 21" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );

  const InterviewProcessIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M4 6H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M4 10H16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M4 14H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );


  const ComplaintsIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 2L3 7V18H17V7L10 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M8 12H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M8 8H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );

  const IntegrationsIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M10 3.5C10 2.67157 9.32843 2 8.5 2C7.67157 2 7 2.67157 7 3.5V6.5C7 7.32843 7.67157 8 8.5 8C9.32843 8 10 7.32843 10 6.5V3.5Z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10 13.5C10 12.6716 9.32843 12 8.5 12C7.67157 12 7 12.6716 7 13.5V16.5C7 17.3284 7.67157 18 8.5 18C9.32843 18 10 17.3284 10 16.5V13.5Z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M17 10C17 10.8284 16.3284 11.5 15.5 11.5H12.5C11.6716 11.5 11 10.8284 11 10C11 9.17157 11.6716 8.5 12.5 8.5H15.5C16.3284 8.5 17 9.17157 17 10Z" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M9 10C9 10.8284 8.32843 11.5 7.5 11.5H4.5C3.67157 11.5 3 10.8284 3 10C3 9.17157 3.67157 8.5 4.5 8.5H7.5C8.32843 8.5 9 9.17157 9 10Z" stroke="currentColor" strokeWidth="1.5"/>
    </svg>
  );

  const LimitsIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5"/>
      <path d="M10 6v4l2.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );

  const LogsIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
      <path d="M5 4.5H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M5 8.5H15" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M5 12.5H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M5 16H9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );

  const managementItems = [
    { id: 'integrations' as const, label: 'Настройки интеграций', icon: IntegrationsIcon },
    { id: 'personal' as const, label: 'Личная информация', icon: PersonalIcon },
    { id: 'branding' as const, label: 'Брендирование', icon: BrandingIcon },
    { id: 'interview-process' as const, label: 'Процесс интервью', icon: InterviewProcessIcon },
    { id: 'limits' as const, label: 'Статистика и лимиты', icon: LimitsIcon },
    ...(atsAvailable ? [{ id: 'logs' as const, label: 'Логи', icon: LogsIcon }] : []),
    ...(isSuperAdmin ? [{ id: 'complaints' as const, label: 'Жалобы', icon: ComplaintsIcon }] : []),
    ...(canManageAccounts ? [{ id: 'accounts' as const, label: 'Управление аккаунтами', icon: AccountIcon }] : []),
    ...(isSuperAdmin ? [{ id: 'companies' as const, label: 'Компании', icon: AccountIcon }] : []),
  ].filter((item) => availableTabs.includes(item.id));
  

  return (
    <div className="relative h-[calc(100vh-57px)] min-h-0 overflow-hidden">
      <div className="relative h-full min-h-0">
                  <div className="box-border content-start flex flex-nowrap gap-4 items-stretch justify-start p-0 relative w-full h-full min-h-0">
                     {/* Центрирующий контейнер для контента (ширина и отступы задаёт Layout) */}
           <div className="w-full h-full min-h-0">
            <div className="flex gap-4 w-full h-full items-stretch min-h-0">
              {/* Management Sidebar - Левая панель с заголовком и табами (скрыта при открытой карточке компании) */}
              {!companyDetailOpen && (
                             <div className="bg-[#f5f6f1] box-border content-stretch flex flex-col gap-6 items-start justify-start p-[24px] rounded-[24px] shrink-0 w-[460px] self-stretch min-h-0 overflow-hidden">
                {/* Sidebar Header */}
                <div className="box-border content-stretch flex flex-row gap-2.5 items-center justify-start px-2 py-0 relative shrink-0">
                  <div className="css-sqkidj font-['Inter_Display:Medium',_sans-serif] leading-[0] not-italic relative shrink-0 text-[#000000] text-[24px] text-left text-nowrap">
                    <p className="block leading-[30px] whitespace-pre">Управление</p>
                  </div>
                </div>
                
                {/* Content Toggle - Табы в левой панели с оригинальными стилями */}
                <div className="box-border content-stretch flex flex-col gap-3 items-start justify-start p-0 relative shrink-0 w-full">
                  {managementItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeSection === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => {
                          setActiveSection(item.id as ManagementSection);
                          // REL-223: переход в раздел из меню — сбрасываем выбранную компанию
                          // (показываем свою), чтобы не «залипал» контекст ранее открытой компании.
                          setLimitsCompanyId(null);
                          if (item.id !== 'accounts') {
                            setInvitationMode(false);
                          }
                        }}
                        className={`${
                          isActive
                            ? 'bg-[#ffffff] border'
                            : 'bg-[#ffffff] border border-transparent hover:border-[#e2e4e9]'
                        } box-border content-stretch flex flex-row gap-3 items-center justify-start p-4 relative rounded-2xl shrink-0 w-full transition-all duration-200`}
                        style={isActive ? { borderColor: colors.primary } : undefined}
                      >
                        <div className="w-[20px] h-[20px] flex items-center justify-center" style={{ color: isActive ? colors.primary : '#525866' }}>
                          <Icon />
                        </div>
                        <div className={`font-['Inter:Medium',_sans-serif] font-medium leading-[0] not-italic relative shrink-0 text-[16px] text-left text-nowrap tracking-[-0.176px]`} style={{ color: isActive ? colors.primary : '#525866' }}>
                          <p className="adjustLetterSpacing block leading-[24px] whitespace-pre">{item.label}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Main Content - Правая панель с контентом вкладок (на всю ширину при открытой карточке) */}
              <div
                className={`box-border content-stretch flex flex-col gap-6 items-stretch justify-start p-0 relative shrink-0 flex-1 self-stretch min-h-0 max-h-full overflow-hidden ${companyDetailOpen ? 'w-full' : 'w-[1110px]'}`}
              >
                {/* Tab Content */}
                <div className="w-full h-full min-h-0">
                  {activeSection === 'interview-process' && availableTabs.includes('interview-process') && <InterviewProcessPage />}
                  {activeSection === 'branding' && availableTabs.includes('branding') && <BrandingPage />}
                  {activeSection === 'limits' && availableTabs.includes('limits') && (
                    <CreditsLimitsContent companyId={limitsCompanyId ?? undefined} />
                  )}
                  {activeSection === 'integrations' && availableTabs.includes('integrations') && <IntegrationsContent />}
                  {activeSection === 'logs' && availableTabs.includes('logs') && <AtsImportLogsPanel />}
                  {activeSection === 'accounts' && availableTabs.includes('accounts') && (
                    <>
                      {console.log('=== Rendering AccountManagementContent ===', { invitationMode, currentTeamId })}
                      <AccountManagementContent invitationMode={invitationMode} teamId={invitationMode ? currentTeamId : null} onTeamMembersUpdate={refreshTeamMembers} />
                    </>
                  )}
                  {activeSection === 'personal' && availableTabs.includes('personal') && <PersonalInfoPage />}
                  {activeSection === 'companies' && isSuperAdmin && availableTabs.includes('companies') && (
                    <CompaniesManagementContent
                      onDetailOpenChange={setCompanyDetailOpen}
                      onOpenCompanyLimits={(id) => {
                        // REL-223: клик по названию компании → раздел «Статистика и лимиты» по ней
                        if (!availableTabs.includes('limits')) return;
                        setLimitsCompanyId(id);
                        setActiveSection('limits');
                      }}
                    />
                  )}
                  {activeSection === 'complaints' && isSuperAdmin && availableTabs.includes('complaints') && <ComplaintsPage />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}